import { SemanticColor, semanticColors } from '../../../theme/colors';
import { TButtonOption } from '../../../types/options';

type TCircleHierarchy = TButtonOption['hierarchy'];

const TEXT_COLOR: Record<string, string> = {
    highlighted: 'var(--white_base)',
    primary: 'var(--white_base)',
    secondary: 'var(--purple_100)',
    tertiary: 'var(--black)',
    destructive: 'var(--pink_100)',
};
const TEXT_DISABLED_COLOR: Record<string, string> = {
    highlighted: 'var(--white)',
    primary: 'var(--white)',
    secondary: 'var(--purple_40_lc)',
    tertiary: 'var(--grey_40_lc)',
    destructive: 'var(--pink_40_lc)',
};

const BG_COLOR: Record<string, string> = {
    primary: semanticColors[SemanticColor.FillPrimary],
    secondary: semanticColors[SemanticColor.FillSecondary],
    tertiary: semanticColors[SemanticColor.FillTertiary],
    tertiaryGlass: semanticColors[SemanticColor.FillTertiaryOnDark],
    destructive: semanticColors[SemanticColor.FillTertiary],
    highlighted: 'var(--gradient-blue_1)',
};

const BG_DISABLED_COLOR: Record<string, string> = {
    primary: semanticColors[SemanticColor.FillPrimaryDisabled],
    secondary: semanticColors[SemanticColor.FillSecondaryDisabled],
    tertiary: semanticColors[SemanticColor.FillTertiaryDisabled],
    tertiaryGlass: semanticColors[SemanticColor.FillTertiaryOnDarkDisabled],
    destructive: semanticColors[SemanticColor.FillTertiaryDisabled],
    highlighted: 'var(--purple_16_lc)',
};

const BG_HOVER_COLOR: Record<string, string> = {
    primary: semanticColors[SemanticColor.FillPrimaryHovered],
    secondary: semanticColors[SemanticColor.FillSecondaryHovered],
    tertiary: semanticColors[SemanticColor.FillTertiaryHovered],
    tertiaryGlass: semanticColors[SemanticColor.FillTertiaryOnDarkHovered],
    destructive: semanticColors[SemanticColor.FillTertiaryHovered],
    highlighted: 'var(--purple_72_hc)',
};

const BG_PRESSED_COLOR: Record<string, string> = {
    primary: semanticColors[SemanticColor.FillPrimaryPressed],
    secondary: semanticColors[SemanticColor.FillSecondaryPressed],
    tertiary: semanticColors[SemanticColor.FillTertiaryPressed],
    tertiaryGlass: semanticColors[SemanticColor.FillTertiaryOnDarkPressed],
    destructive: semanticColors[SemanticColor.FillTertiaryPressed],
    highlighted: 'var(--purple_72_tr)',
};

const ICON_COLOR: Record<string, string> = {
    primary: semanticColors[SemanticColor.IconPrimaryInverted],
    emphasised: semanticColors[SemanticColor.IconEmphasised],
    secondary: semanticColors[SemanticColor.IconEmphasised],
    tertiary: semanticColors[SemanticColor.IconPrimary],
    tertiaryGlass: semanticColors[SemanticColor.IconPrimaryOnDarkFill],
    destructive: semanticColors[SemanticColor.IconError],
};

const ICON_DISABLED_COLOR: Record<string, string> = {
    primary: semanticColors[SemanticColor.IconPrimaryInvertedDisabled],
    emphasised: semanticColors[SemanticColor.IconEmphasisedDisabled],
    secondary: semanticColors[SemanticColor.IconEmphasisedDisabled],
    tertiary: semanticColors[SemanticColor.IconPrimaryDisabled],
    tertiaryGlass: semanticColors[SemanticColor.IconPrimaryOnDarkFillDisabled],
    destructive: semanticColors[SemanticColor.IconErrorDisabled],
};

const key = (h: TCircleHierarchy): string => h ?? 'primary';

export const getTextColor = (h: TCircleHierarchy): string => TEXT_COLOR[key(h)] ?? 'var(--black)';

export const getTextColorDisabled = (h: TCircleHierarchy): string => TEXT_DISABLED_COLOR[key(h)] ?? 'var(--black)';

export const getCircleBg = (h: TCircleHierarchy, buttonColor?: string[]): string => {
    if (h === 'highlighted' && buttonColor?.length) {
        return `linear-gradient(180deg, ${buttonColor.join(', ')})`;
    }
    return BG_COLOR[key(h)] ?? 'var(--white)';
};

export const getCircleBgDisabled = (h: TCircleHierarchy): string => BG_DISABLED_COLOR[key(h)] ?? 'var(--white)';

export const getCircleHoverBg = (h: TCircleHierarchy): string => BG_HOVER_COLOR[key(h)] ?? 'var(--grey_16_lc)';

export const getCirclePressedBg = (h: TCircleHierarchy): string => BG_PRESSED_COLOR[key(h)] ?? 'var(--grey_8_lc)';

export const getCircleIconColor = (h: TCircleHierarchy): string => ICON_COLOR[key(h)] ?? 'var(--black)';

export const getCircleIconColorDisabled = (h: TCircleHierarchy): string => ICON_DISABLED_COLOR[key(h)] ?? 'var(--black)';
